import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {css} from "@emotion/react";
import {Button} from "@pg-design/button";
import {flex, flexAlignCenter, flexDirection, mb, mt, pointer, pv, textAlign, underline} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";

import {useAppDispatch} from "../../../../../../../utils/hooks/store_hooks";
import {setAuthModal} from "../../../../auth/auth_slice";
import {EntryVisibility} from "../../../types/Entry";
import {BigDataInsightSubscriptionModal} from "./BigDataInsightSubscriptionModal";

interface IArticlePaywallPlaceholderProps {
    visibility: EntryVisibility;
}

export const ArticlePaywallPlaceholder = ({visibility}: IArticlePaywallPlaceholderProps) => {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
    const {pathname, search} = useLocation();
    const returnPath = `${pathname}${search}`;

    const handleLogin = () => {
        dispatch(setAuthModal("login"));
    };

    const handleSubscribe = () => {
        setIsSubscriptionModalOpen(true);
    };

    return (
        <>
            <div css={wrapper}>
                <div css={topBorder} />

                <div css={content}>
                    <Text variant="headline_4" css={[textAlign("center"), mb(3)]}>
                        {t("newshub.paywall.message")}
                    </Text>

                    {visibility === "paid_only" && (
                        <Button
                            variant="outlined_secondary"
                            size="medium"
                            onClick={handleSubscribe}
                            css={[subscribeButton, mb(3)]}
                        >
                            {t("newshub.paywall.subscribe_cta")}
                        </Button>
                    )}

                    <Text variant="body_copy_1" as="div" css={textAlign("center")}>
                        {t("newshub.paywall.login_prompt")}{" "}
                        <span role="button" tabIndex={0} css={loginLink} onClick={handleLogin}>
                            {t("newshub.paywall.login_link")}
                        </span>
                    </Text>
                </div>

                <div css={bottomBorder} />
            </div>

            <BigDataInsightSubscriptionModal
                isOpen={isSubscriptionModalOpen}
                onClose={() => setIsSubscriptionModalOpen(false)}
                returnPath={returnPath}
            />
        </>
    );
};

const wrapper = css`
    ${flex()};
    ${flexDirection("column")};
    ${flexAlignCenter};
    ${pv(4)};
`;

const topBorder = css`
    width: 100%;
    height: 4px;
    background-color: #1a1a1e;
    margin-bottom: 3.2rem;
`;

const bottomBorder = css`
    width: 100%;
    height: 1px;
    background-color: #e0e0e0;
    ${mt(4)};
`;

const content = css`
    ${flex()};
    ${flexDirection("column")};
    ${flexAlignCenter};
    width: 100%;
`;

const subscribeButton = css`
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const loginLink = css`
    ${underline};
    ${pointer};
    font-weight: 600;
`;
